/**
 * PotentialEarningsPanel — two variants:
 *
 *  'landing'   — anonymous aspiration bar using public LoyaltyStats.
 *                Receives `stats` as a prop (fetched by parent). Shows
 *                "Earn up to R200,000 / $20,000" with community total and
 *                a "Register Free" CTA.
 *
 *  'dashboard' — authenticated personalised progress bar using LoyaltyResponse.
 *                Receives `loyalty` prop. Shows unlocked vs potential with
 *                ZAR/USD milestone markers, complete state, and zero state.
 *
 * i18n namespace : potential_earnings
 * Design         : aurora gradient bar (violet → pink → orange), white bg.
 *                  NO dark backgrounds. NO gold.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LoyaltyStats, LoyaltyResponse } from '../services/api';

// ── Milestone thresholds ──────────────────────────────────────────────────────

/** ZAR milestone amounts shown as pips below the dashboard bar. */
const ZAR_MILESTONES = [500, 1_000, 5_000, 10_000, 50_000, 100_000, 200_000];

/** USD milestone amounts shown as pips below the dashboard bar. */
const USD_MILESTONES = [50, 100, 500, 1_000, 5_000, 10_000, 20_000];

// ── Currency formatter — ISO 4217 safe ───────────────────────────────────────

/**
 * Formats `value` as currency. Guards against empty/invalid ISO 4217 codes
 * that would cause `Intl.NumberFormat` to throw a RangeError.
 */
function fmtCurrency(value: number, currency: string): string {
  const safeCurrency =
    currency && /^[A-Z]{3}$/.test(currency) ? currency : 'ZAR';
  return new Intl.NumberFormat(safeCurrency === 'ZAR' ? 'en-ZA' : 'en-US', {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// ── Aurora progress bar ───────────────────────────────────────────────────────

interface AuroraBarProps {
  /** 0–100 fill percentage. Clamped automatically. */
  pct: number;
  /** Accessible label forwarded to the progressbar role. */
  'aria-label'?: string;
}

function AuroraBar({ pct, 'aria-label': ariaLabel }: AuroraBarProps) {
  // Number.isFinite() rejects NaN and ±Infinity — both would produce an invalid
  // CSS width value if passed directly to Math.max/Math.min.
  const clampedPct = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clampedPct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      className="relative h-3 w-full rounded-full bg-gray-100 overflow-hidden"
    >
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
        style={{
          width: `${clampedPct}%`,
          background:
            'linear-gradient(90deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)',
        }}
      />
    </div>
  );
}

// ── Milestone pip ─────────────────────────────────────────────────────────────

interface MilestonePipProps {
  amount: number;
  currency: string;
  /** Position as a percentage (0–100) of the bar width. */
  pct: number;
  /** Whether the unlocked amount has already passed this milestone. */
  reached: boolean;
}

function MilestonePip({ amount, currency, pct, reached }: MilestonePipProps) {
  return (
    <div
      data-testid="milestone-pip"
      className="absolute flex flex-col items-center"
      style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
    >
      <div
        className={`w-1.5 h-1.5 rounded-full mt-1 ${
          reached ? 'bg-violet-500' : 'bg-gray-300'
        }`}
      />
      <span
        className={`text-[9px] font-semibold mt-0.5 whitespace-nowrap leading-none ${
          reached ? 'text-violet-600' : 'text-gray-400'
        }`}
      >
        {fmtCurrency(amount, currency)}
      </span>
    </div>
  );
}

// ── Landing variant ───────────────────────────────────────────────────────────

function LandingPanel({ stats }: { stats: LoyaltyStats | null }) {
  const { t } = useTranslation();

  // Fall back to spec-defined maxima when stats haven't loaded yet.
  const maxZar = stats?.max_potential_zar ?? 200_000;
  const maxUsd = stats?.max_potential_usd ?? 20_000;
  const issuedZar = stats?.total_issued_zar ?? 0;
  const issuedUsd = stats?.total_issued_usd ?? 0;
  // Member count is intentionally NOT shown on the public landing page (admin-only).

  // Aspiration bar: how much of the community max has been issued so far.
  const fillPct = maxZar > 0 ? Math.min(100, (issuedZar / maxZar) * 100) : 0;

  return (
    <section
      className="rounded-2xl border border-violet-100 bg-white p-6 shadow-sm"
      aria-label={t('potential_earnings.landing_aria')}
    >
      <h3
        className="font-heading text-lg font-bold text-violet-900 mb-1"
      >
        {t('potential_earnings.landing_headline', {
          zar: fmtCurrency(maxZar, 'ZAR'),
          usd: fmtCurrency(maxUsd, 'USD'),
        })}
      </h3>
      {/* Subline: only shown once stats have loaded — avoids "0 members / R0 earned" flash */}
      {stats !== null && (
        <p
          className="text-sm text-gray-500 mb-4"
        >
          {t('potential_earnings.landing_subline', {
            issued_zar: fmtCurrency(issuedZar, 'ZAR'),
            issued_usd: fmtCurrency(issuedUsd, 'USD'),
          })}
        </p>
      )}

      <AuroraBar
        pct={fillPct}
        aria-label={t('potential_earnings.bar_aria', {
          pct: Math.round(fillPct),
        })}
      />

      <div className="mt-4 text-center">
        <Link
          to="/register"
          className="inline-flex items-center justify-center min-h-[44px] px-6 py-2 rounded-xl font-bold text-sm text-white transition hover:opacity-90"
          style={{
            background: 'linear-gradient(90deg, #7c3aed 0%, #ec4899 100%)',
          }}
        >
          {t('potential_earnings.landing_cta')}
        </Link>
      </div>
    </section>
  );
}

// ── Dashboard variant ─────────────────────────────────────────────────────────

function DashboardPanel({ loyalty }: { loyalty: LoyaltyResponse | null }) {
  const { t } = useTranslation();

  if (loyalty === null) {
    return (
      <section
        className="rounded-2xl border border-violet-100 bg-white p-6 shadow-sm"
        aria-label={t('potential_earnings.dashboard_aria')}
        aria-busy="true"
      >
        <div className="h-4 w-48 animate-pulse rounded bg-violet-100 mb-4" />
        <div className="h-3 rounded-full bg-gray-100 animate-pulse" />
      </section>
    );
  }

  // Validate wallet_currency to prevent Intl.NumberFormat RangeError.
  const currency =
    loyalty.wallet_currency && /^[A-Z]{3}$/.test(loyalty.wallet_currency)
      ? loyalty.wallet_currency
      : 'ZAR';

  const milestones = currency === 'ZAR' ? ZAR_MILESTONES : USD_MILESTONES;

  const potentialNum = parseFloat(loyalty.potential);
  const unlockedNum = parseFloat(loyalty.unlocked);

  const safePotential = isNaN(potentialNum) ? 0 : potentialNum;
  const safeUnlocked = isNaN(unlockedNum) ? 0 : unlockedNum;

  const isZero = safePotential <= 0;
  const isComplete = !isZero && safeUnlocked >= safePotential;
  const fillPct = isZero
    ? 0
    : Math.min(100, (safeUnlocked / safePotential) * 100);

  // Only render milestones that fall within the user's cap range.
  const visibleMilestones = milestones.filter((m) => m <= safePotential);

  return (
    <section
      className="rounded-2xl border border-violet-100 bg-white p-6 shadow-sm"
      aria-label={t('potential_earnings.dashboard_aria')}
    >
      <h3
        className="font-heading text-base font-bold text-violet-900 mb-3"
      >
        {t('potential_earnings.dashboard_title')}
      </h3>

      <AuroraBar
        pct={isComplete ? 100 : fillPct}
        aria-label={t('potential_earnings.bar_aria', {
          pct: isComplete ? 100 : Math.round(fillPct),
        })}
      />

      {/* Milestone markers — decorative, hidden from screen readers */}
      {!isZero && visibleMilestones.length > 0 && (
        <div
          className="relative mt-1 mb-6"
          aria-hidden="true"
        >
          {visibleMilestones.map((m) => (
            <MilestonePip
              key={m}
              amount={m}
              currency={currency}
              pct={(m / safePotential) * 100}
              reached={safeUnlocked >= m}
            />
          ))}
        </div>
      )}

      {/* Status text */}
      {isComplete ? (
        <p
          role="status"
          className="mt-2 text-sm font-semibold text-green-700 text-center"
        >
          {t('potential_earnings.dashboard_complete', {
            amount: fmtCurrency(safeUnlocked, currency),
          })}
        </p>
      ) : (
        <p
          className="mt-2 text-xs text-gray-500 text-center"
        >
          {isZero
            ? t('potential_earnings.dashboard_zero')
            : t('potential_earnings.dashboard_progress', {
                unlocked: fmtCurrency(safeUnlocked, currency),
                potential: fmtCurrency(safePotential, currency),
              })}
        </p>
      )}
    </section>
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

export type PotentialEarningsPanelProps =
  | { variant: 'landing'; stats: LoyaltyStats | null }
  | { variant: 'dashboard'; loyalty: LoyaltyResponse | null };

/**
 * Renders the earnings potential panel in one of two variants:
 * - `landing` — aspirational community stats for anonymous visitors.
 * - `dashboard` — personalised progress bar for authenticated users.
 */
export function PotentialEarningsPanel(props: PotentialEarningsPanelProps) {
  if (props.variant === 'landing') {
    return <LandingPanel stats={props.stats} />;
  }
  return <DashboardPanel loyalty={props.loyalty} />;
}
